import { UserApplicationVO } from '../../vo/user/user-app.vo';
import { UserVO } from '../../vo/user/user.vo';
import { BusinessException, EntityModificationPack, UserApplication } from '@wizeoo/akm-message-lib';

import { WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import { WizIcon, WizPopUp, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { StaticFeatureEnum } from '../../manager/permission/permissions.types';
import { FEATURE_PERIMETER } from '../../model/enums';
import { ProfileVO } from '../../vo/user/profile.vo';
import { EM_ACTION } from '@wizeoo/akm-compute-lib';
import { Model } from '../../model/model';
import { EMEngine } from '../entities/entity-modification.engine';
import { ViewData } from '../../model/view.data';
import { CommitCmd } from '../../command/em/commit.cmd';
import { FeaturePermission } from '../permission/permissions.types';
import { RoleVO } from '../../vo/user/role.vo';
import { FeatureVO } from '../../vo/core/feature.vo';

export type ROLE_OR_PROFILE = ProfileVO | FeaturePermission | RoleVO; // objet manipulé par la vue "droits d'accès"

// rôles non visible en mode allKeyMe (réservés DOM)
export const FILTERED_ROLES: string[] = [StaticFeatureEnum.APP_ADMIN, StaticFeatureEnum.DOM_ADMIN, StaticFeatureEnum.BU_ADMIN, StaticFeatureEnum.BU_VISION_ADMIN, StaticFeatureEnum.DOM_VISION_ADMIN];


/** gestion du user, des profiles, des rôles... */
export class UserAppEngine {
  
  protected _model: Model = null;
  constructor(model: Model) {
    this._model = model;
  }
  public get model(): Model {
    return this._model as Model;
  }



  public newUserApp(
    domain: string,
    bu: string = '*',
    vision: string = '*',
    perimeter: string = null
  ): UserApplicationVO {
    const result = new UserApplicationVO(this.model);
    result.domain = domain;
    result.businessUnitCode = bu;
    result.visionCode = vision;
    result.perimeter = perimeter;

    return result;
  }

  /**
   * Détermine si le role devrait être visible
   * @param kind le role à vérifier
   * @returns True si le rôle doit apparaître false sinon
   */
   public availableRoleKind(kind: string): boolean {
    if (FILTERED_ROLES.includes(kind)) { // pas les méta-rôles
      return false;
    }  
    return kind === FEATURE_PERIMETER.APP;
  } 


  /**
   * Détermine si le role devrait être visible
   * @param item le role à vérifier
   * @returns True si le rôle doit apparaître false sinon
   */
  public availableRoles(item: ROLE_OR_PROFILE | RoleVO): boolean {
      if (FILTERED_ROLES.includes(item.code)) { // pas les méta-rôles
        return false;
      }
    
      if (item instanceof RoleVO && item.isStatic) { // les fonction statiques, uniquement pour le perimètre APP
        return item.perimeter === FEATURE_PERIMETER.APP;
      }
  
      if (item.perimeter !== FEATURE_PERIMETER.APP) {
        return false;
      }
      return true;
  } 
  
  /** définit le code type précis. Pour les profiles, ça reste profile. Pour les rôles, on détaille */
  public getFeatureCode(feat: FeaturePermission): string {
    if (!!feat.dynamic) {
      return feat.code;
    }
    return null;
  }; 


  /** définit le code type précis. Pour les profiles, ça reste profile. Pour les rôles, on détaille */
  public getFeatureKind(feat: FeaturePermission): string {
    const type = feat.doProfile ? 'profile' : 'role';
    let permType = type;
    if (type === 'role') {
        permType += '.' +feat.feature.kind;
    }
    return WizTranslationManager.getInstance().translateKey('$.user.role-profile.' + permType + '.type.$');
  }; 

  
  /**
   * create/ update d'un pays
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  // tslint:disable-next-line: ban-types
  public merge(to: UserApplicationVO, from: UserApplicationVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exist) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (pack) {
      // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(to, WIZ_COMMAND_STATE.SUCCESS); // retour en vue
    }
  }

  public generatePermission(): Map<string, FeaturePermission> {
    const featuresMap: Map<string, FeaturePermission> = new Map(); // map cible par code Feature

     // features statiques (à la BU)
     const staticFeature: Map<string, FeatureVO> = this.model.featureMap.get(ViewData.wildcardDomain);
     [...staticFeature.values()].forEach((f: FeatureVO) => {
       if(this.availableRoleKind(f.perimeter)) {
         const featPerm: FeaturePermission = {
           ...f.source,
           feature: f,
           roles: []
         };
         featPerm.trsCode = this.getFeatureCode(featPerm);
         featPerm.trsKind = this.getFeatureKind(featPerm);
         featPerm.trsLabel = WizTranslationManager.getInstance().translateKey('$.feature.static.' + f.code + '.$');
         featuresMap.set(f.code, featPerm);
       }
     });

     // features de la BU (attention, on veut la vision)
     const buFeature: Map<string, FeatureVO> = this.model.featureMap.get(this.model.businessUnitCode);
     const visionFeature: FeatureVO[] = [];
     [...buFeature.values()].forEach((f: FeatureVO) => {
       if(f.visionCode === this.model.visionCode) { // là, c'est mieux
         visionFeature.push(f);

         const featPerm: FeaturePermission = {
           ...f.source,
           feature: f,
           roles: []
         };
         featPerm.trsCode = this.getFeatureCode(featPerm);
         featPerm.trsKind = this.getFeatureKind(featPerm);
         featPerm.trsLabel = f.trsLabel;
         featuresMap.set(f.code, featPerm);
       }
     });
     // console.log('visionFeature', visionFeature);



     // collecte des rôles
     const baseRoles = this.model.rolesMap.get(ViewData.wildcardDomain).get(ViewData.wildcardDomain);
     let dynaRoles = this.model.rolesMap.get(this.model.businessUnitCode)?.get(this.model.visionCode);
     if(!dynaRoles) {
      dynaRoles = new Map();
     }

     const roles: RoleVO[] = [...baseRoles.values(), ...dynaRoles?.values()];

     roles.forEach((role: RoleVO)=> {
       if(featuresMap.has(role.featureCode)) {
         featuresMap.get(role.featureCode).roles.push(role); // existe, simple ajout du rôle
       }
     });
   
    return featuresMap;
  }


  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  protected backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: UserApplicationVO = cmd.sourceVO[0] as UserApplicationVO;
      // une seule modification à la fois, packEntry donne l'objet
      const source: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = source; // le clone

            // mise à jour des user apps pour les propriétaires dans le model
            const ownerUpdate: UserVO = this.model.userEngine.getUserById(vo.ownerId);
            if (!!ownerUpdate) {
              this.model.userEngine.updateAppOnUser(ownerUpdate, source);
            }

            break;
          case EM_ACTION.CREATE:
            const newAppVO = new UserApplicationVO(this.model, source);

            // mise à jour des user apps pour les propriétaires dans le model
            const ownerCreate: UserVO = this.model.userEngine.getUserById(newAppVO.ownerId);
            if (!!ownerCreate) {
              this.model.userEngine.addAppOnUser(ownerCreate, newAppVO);
            }
            break;
        }
      }

      cmd.viewCallBack(cmd.sourceVO, cmd.result.status); // retour en vue
    }
  }

  /**
   * construction de la clé du userApp
   * @param src 
   * @returns 
   */
  public key(src: UserApplicationVO | UserApplication): string {
    if (!src) {
      return '';
    }

    return `${src.domain}|${src.businessUnitCode}|${src.visionCode}|${src.perimeter}|${src.ownerId}`;
  }

  /**
   * Clé business sans le ownerId
   * @param src 
   * @returns 
   */
  public businessKey(src: UserApplicationVO | UserApplication): string {
    return `${src.domain}|${src.businessUnitCode}|${src.visionCode}|${src.perimeter}`;
  }

}
